import { Props } from './interfaces'
import * as Styled from './styled'
import { get } from 'lodash'
import { useDeviceDetect, useTranslator } from '~hooks'
import ActionMenu from './components/ActionMenu'
import { Skeleton } from '~components'
import { FuelTypography } from '@frete.com/fuel-react'
import { Text } from '~constants/text'
import { getCustomToken } from '~utils/token'

function DataTable({
  mobileColumns,
  columns,
  rows,
  actions,
  showActionOnlyWhenMatch,
  comparisonProp,
  comparisonValue,
  loading,
  noDataComponent = null,
}: Props) {
  const { isMobile } = useDeviceDetect()
  const actionMenuSize = '198px'
  const rowsOnLoader = new Array(5).fill('')
  const { translate } = useTranslator()

  function renderActions(row: object) {
    return actions && (!showActionOnlyWhenMatch || (showActionOnlyWhenMatch && row[comparisonProp] === comparisonValue))
  }

  return (
    <>
      {loading ? (
        <Styled.LoaderWrapper>
          <Styled.LoaderRowWrapper>
            <Skeleton $width="100%" $height="50px" isLoading={true} />
          </Styled.LoaderRowWrapper>

          {rowsOnLoader.map(() => (
            <Styled.LoaderHeaderWrapper key={getCustomToken()}>
              {columns.length ? (
                columns.map(() => <Skeleton key={getCustomToken()} $width="90%" $height="40px" isLoading={true} />)
              ) : (
                <>
                  <Skeleton $width="100%" $height="40px" isLoading={true} />
                  <Skeleton $width="100%" $height="40px" isLoading={true} />
                  <Skeleton $width="100%" $height="40px" isLoading={true} />
                  <Skeleton $width="100%" $height="40px" isLoading={true} />
                  <Skeleton $width="100%" $height="40px" isLoading={true} />
                </>
              )}
            </Styled.LoaderHeaderWrapper>
          ))}
        </Styled.LoaderWrapper>
      ) : (
        <>
          {isMobile && mobileColumns?.length ? (
            <Styled.MobileWrapper data-testid="table_mobile">
              {rows.map((row, index) => (
                <Styled.MobileRowContent key={getCustomToken()}>
                  <Styled.MobileRowData>
                    {columns
                      .filter((column) => mobileColumns.includes(column.field))
                      .map((column, columnIndex) => (
                        <Styled.Label
                          $addElipsis
                          variant={columnIndex === 0 ? 'bold' : 'regular'}
                          data-testid={`register-${index + 1}`}
                          key={getCustomToken()}
                        >
                          {get(row, column.field)}
                        </Styled.Label>
                      ))}
                  </Styled.MobileRowData>
                  {renderActions(row) && (
                    <Styled.MobileAction>
                      <ActionMenu
                        width={actionMenuSize}
                        actions={actions}
                        row={row}
                        comparisonProp={comparisonProp}
                        comparisonValue={comparisonValue}
                      />
                    </Styled.MobileAction>
                  )}
                </Styled.MobileRowContent>
              ))}
            </Styled.MobileWrapper>
          ) : (
            <Styled.DataTable data-testid="table">
              <Styled.Header>
                <tr>
                  {columns.map((column) => (
                    <Styled.Column $width={column.width} key={getCustomToken()}>
                      <FuelTypography color="neutral-default" tag="p" size="caption">
                        {column.name}
                      </FuelTypography>
                    </Styled.Column>
                  ))}
                  {actions && <Styled.Column $width={20} />}
                </tr>
              </Styled.Header>
              <Styled.Body>
                {rows?.map((row, index) => (
                  <Styled.Row key={getCustomToken()} data-testid={`register-${index + 1}`}>
                    {columns.map((column) => (
                      <Styled.Cell key={getCustomToken()} $width={column.width} $fontWeight={column.fontWeight}>
                        <FuelTypography tag="p" size="body-sm">
                          {get(row, column.field)}
                        </FuelTypography>
                      </Styled.Cell>
                    ))}
                    {renderActions(row) && (
                      <Styled.Cell $width={20} $textAlign="right">
                        <ActionMenu
                          width={actionMenuSize}
                          actions={actions}
                          row={row}
                          comparisonProp={comparisonProp}
                          comparisonValue={comparisonValue}
                        />
                      </Styled.Cell>
                    )}
                  </Styled.Row>
                ))}
              </Styled.Body>
            </Styled.DataTable>
          )}

          {!rows.length && (
            <Styled.NoDataWrapper>
              {noDataComponent || (
                <FuelTypography tag="p" color="neutral-default" size="body-lg">
                  {translate(Text.NO_DATA_TO_DISPLAY)}
                </FuelTypography>
              )}
            </Styled.NoDataWrapper>
          )}
        </>
      )}
    </>
  )
}

export default DataTable
